import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { billingTypeLabel, formatCurrency, formatItemPricingDetail } from "@/domain/billing";
import { formatDate, formatDateRange } from "@/domain/dates";
import type { InvoiceRecord } from "@/domain/types";
import { preferredEnabledPaymentQrMethod } from "@/features/invoices/payment-qr";
import { addressLines, drawFooter, drawPdfBrandMark, partyLines, PDF_COLORS } from "./pdf-helpers";

function itemDetail(
  invoice: InvoiceRecord,
  index: number,
): string {
  const item = invoice.items[index]!;
  return formatItemPricingDetail(
    item,
    invoice.currency,
  );
}

async function imageUrlToDataUrl(url: string): Promise<string> {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Could not load QR image (${response.status}).`);
  }

  const blob = await response.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Could not read QR image."));
    reader.readAsDataURL(blob);
  });
}

async function appendPaymentQrPage(
  doc: jsPDF,
  invoice: InvoiceRecord,
): Promise<void> {
  if (invoice.totals.balanceCents <= 0) {
    return;
  }

  const preferred = preferredEnabledPaymentQrMethod(
    invoice.paymentQr,
    invoice.paymentQrAssets,
  );

  if (!preferred) {
    return;
  }

  const asset = invoice.paymentQrAssets[preferred];
  if (!asset.imageUrl) return;

  try {
    const imageData = await imageUrlToDataUrl(asset.imageUrl);
    doc.addPage();
    doc.setFillColor(...PDF_COLORS.charcoal);
    doc.rect(0, 0, 210, 44, "F");
    doc.setFillColor(...PDF_COLORS.gold);
    doc.rect(0, 0, 4, 297, "F");
    doc.setFillColor(...PDF_COLORS.mint);
    doc.rect(4, 0, 1.5, 297, "F");
    drawPdfBrandMark(doc, 14, 9, 13);

    doc.setFont("helvetica", "bold");
    doc.setTextColor(...PDF_COLORS.white);
    doc.setFontSize(18);
    doc.text("PAY THIS INVOICE", 194, 18, {
      align: "right",
    });
    doc.setTextColor(...PDF_COLORS.gold);
    doc.setFontSize(9);
    doc.text(invoice.invoiceNumber, 194, 26, {
      align: "right",
    });

    doc.setTextColor(...PDF_COLORS.ink);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text(asset.label, 105, 56, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...PDF_COLORS.muted);
    doc.setFontSize(8.5);
    doc.text(asset.accountLabel, 105, 63, {
      align: "center",
    });

    const maxWidth = 92;
    const maxHeight = 150;
    const imageWidth = Math.min(
      maxWidth,
      maxHeight / asset.aspectRatio,
    );
    const imageHeight = imageWidth * asset.aspectRatio;
    const imageX = (210 - imageWidth) / 2;
    const imageY = 70;

    doc.addImage(
      imageData,
      "PNG",
      imageX,
      imageY,
      imageWidth,
      imageHeight,
      undefined,
      "FAST",
    );

    const summaryY = Math.min(239, imageY + imageHeight + 10);
    doc.setFillColor(...PDF_COLORS.mintPale);
    doc.roundedRect(32, summaryY, 146, 31, 3, 3, "F");
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...PDF_COLORS.ink);
    doc.setFontSize(8);
    doc.text(
      invoice.currency === "PHP"
        ? "AMOUNT TO SEND"
        : "INVOICE BALANCE",
      40,
      summaryY + 9,
    );
    doc.text("PAYMENT REFERENCE", 40, summaryY + 21);
    doc.setTextColor(...PDF_COLORS.gold);
    doc.setFontSize(12);
    doc.text(
      formatCurrency(
        invoice.totals.balanceCents,
        invoice.currency,
      ),
      170,
      summaryY + 9,
      { align: "right" },
    );
    doc.setTextColor(...PDF_COLORS.ink);
    doc.setFontSize(10);
    doc.text(invoice.invoiceNumber, 170, summaryY + 21, {
      align: "right",
    });

    doc.setFont("helvetica", "normal");
    doc.setTextColor(...PDF_COLORS.muted);
    doc.setFontSize(8);
    const qrPaymentNote =
      invoice.currency === "PHP"
        ? "After payment, send the transaction reference or a screenshot for confirmation."
        : `${invoice.currency} invoice. This QR receives PHP - use only the agreed converted PHP amount, then send the transaction reference.`;
    doc.text(
      doc.splitTextToSize(qrPaymentNote, 160),
      105,
      summaryY + 41,
      { align: "center", lineHeightFactor: 1.35 },
    );
    drawFooter(doc, invoice.invoiceNumber);
  } catch (error) {
    console.warn("Could not append invoice payment QR page.", error);
  }
}

export async function downloadInvoicePdf(
  invoice: InvoiceRecord,
): Promise<void> {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  doc.setFillColor(...PDF_COLORS.charcoal);
  doc.rect(0, 0, 210, 62, "F");
  doc.setFillColor(...PDF_COLORS.gold);
  doc.rect(0, 0, 4, 297, "F");
  doc.setFillColor(...PDF_COLORS.mint);
  doc.rect(4, 0, 1.5, 297, "F");

  drawPdfBrandMark(doc, 14, 9, 13);

  doc.setFont("helvetica", "bold");
  doc.setTextColor(...PDF_COLORS.white);
  doc.setFontSize(15);
  doc.text(invoice.company.name.toUpperCase(), 31, 18);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(190, 217, 211);
  doc.text(invoice.company.tagline || "Professional Services", 31, 24);
  doc.text(invoice.company.email, 31, 29);

  doc.setFont("helvetica", "bold");
  doc.setTextColor(...PDF_COLORS.white);
  doc.setFontSize(28);
  doc.text("INVOICE", 194, 20, { align: "right" });
  doc.setTextColor(...PDF_COLORS.gold);
  doc.setFontSize(10);
  doc.text(invoice.invoiceNumber, 194, 28, { align: "right" });

  const fields = [
    ["ISSUE DATE", formatDate(invoice.issueDate)],
    ["SERVICE PERIOD", formatDateRange(invoice.serviceStartDate, invoice.serviceEndDate)],
    ["PAYMENT DUE", invoice.dueDate ? formatDate(invoice.dueDate) : invoice.dueLabel],
  ];
  fields.forEach(([label, value], index) => {
    const x = 16 + index * 61;
    doc.setFontSize(7);
    doc.setTextColor(...PDF_COLORS.gold);
    doc.text(label!, x, 47);
    doc.setFontSize(8.5);
    doc.setTextColor(...PDF_COLORS.white);
    const lines = doc.splitTextToSize(value!, 54) as string[];
    doc.text(lines, x, 52);
  });

  doc.setTextColor(...PDF_COLORS.ink);
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.text("BILLED TO", 16, 75);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(partyLines(invoice.client), 16, 81, { lineHeightFactor: 1.45 });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text("FROM", 112, 75);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text([invoice.company.name, invoice.company.company, invoice.company.email, invoice.company.phone, ...addressLines(invoice.company.address)].filter(Boolean), 112, 81, { lineHeightFactor: 1.45 });

  autoTable(doc, {
    startY: 111,
    margin: { left: 16, right: 16 },
    head: [["SERVICE / DELIVERABLE", "BILLING", "DETAIL", "AMOUNT"]],
    body: invoice.items.map((item, index) => [item.description, billingTypeLabel(item.billingType), itemDetail(invoice, index), formatCurrency(item.amountCents, invoice.currency)]),
    theme: "grid",
    headStyles: { fillColor: PDF_COLORS.charcoal2, textColor: PDF_COLORS.white, fontSize: 7.5, fontStyle: "bold", cellPadding: 3 },
    bodyStyles: { textColor: PDF_COLORS.ink, fontSize: 8, cellPadding: 3.2, lineColor: PDF_COLORS.border, lineWidth: 0.2 },
    alternateRowStyles: { fillColor: [247, 249, 248] },
    columnStyles: { 0: { cellWidth: 72 }, 1: { cellWidth: 26 }, 2: { cellWidth: 46 }, 3: { cellWidth: 34, halign: "right", fontStyle: "bold" } },
  });

  const finalY = (doc as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? 150;
  const totalsY = Math.min(245, finalY + 10);
  doc.setFillColor(...PDF_COLORS.mintPale);
  doc.roundedRect(16, totalsY, 100, 40, 3, 3, "F");
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...PDF_COLORS.ink);
  doc.setFontSize(8);
  doc.text("PAYMENT TERMS", 21, totalsY + 9);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.2);
  const publicText = [invoice.paymentTerms, invoice.paymentInstructions, invoice.publicNotes].filter(Boolean).join("\n");
  doc.text(doc.splitTextToSize(publicText || "No additional payment instructions.", 88), 21, totalsY + 16, { lineHeightFactor: 1.35 });

  const x = 132;
  doc.setFontSize(8);
  doc.setTextColor(...PDF_COLORS.muted);
  doc.text("SUBTOTAL", x, totalsY + 8);
  doc.text("TAX", x, totalsY + 17);
  doc.setTextColor(...PDF_COLORS.ink);
  doc.setFont("helvetica", "bold");
  doc.text(formatCurrency(invoice.totals.subtotalCents, invoice.currency), 194, totalsY + 8, { align: "right" });
  doc.text(formatCurrency(invoice.totals.taxCents, invoice.currency), 194, totalsY + 17, { align: "right" });
  doc.setDrawColor(...PDF_COLORS.border);
  doc.line(x, totalsY + 22, 194, totalsY + 22);
  doc.setFontSize(9);
  doc.text(`TOTAL DUE (${invoice.currency})`, x, totalsY + 31);
  doc.setTextColor(...PDF_COLORS.gold);
  doc.setFontSize(16);
  doc.text(formatCurrency(invoice.totals.balanceCents, invoice.currency), 194, totalsY + 32, { align: "right" });

  drawFooter(doc, invoice.invoiceNumber);
  await appendPaymentQrPage(doc, invoice);
  doc.save(`${invoice.invoiceNumber}.pdf`);
}
